import React from 'react';
import { useTranslation } from 'react-i18next';
import { limitRows } from '../../services/planUsage';
import { usePlanLimitStore } from '../../store/planLimitStore';
import { setUpgradeModalOpen, setUpgradeReason } from '../../store/uiModalStore';
import LocalCapsSection from '../plans/LocalCapsSection';

const BAR_COLOR = { ok: '#6366f1', near: '#f59e0b', full: '#ef4444' };

/**
 * Plan limits inside the titlebar sync menu: the plan's LOCAL caps (Plans v2
 * card 3.3, `localRows` from useLocalCapRows: "3 / 1 boards: upgrade to Basic
 * for unlimited"), a progress bar per cloud limit, the last limit refusal, and
 * an Upgrade button. Renders nothing for plans without limits.
 */
export default function PlanLimitsPanel({ usage, isDark, onClose, localRows = [] }) {
  const { t } = useTranslation();
  const lastRefusal = usePlanLimitStore((s) => s.lastRefusal);
  const deviceNotice = usePlanLimitStore((s) => s.deviceNotice);
  const dismiss = usePlanLimitStore((s) => s.dismiss);
  const dismissDeviceNotice = usePlanLimitStore((s) => s.dismissDeviceNotice);
  const rows = limitRows(usage);
  const hasCloud = rows.length > 0 || Boolean(lastRefusal) || Boolean(deviceNotice);
  if (!hasCloud && localRows.length === 0) return null;

  const muted = isDark ? '#a1a1aa' : '#6b7280';
  const strong = isDark ? '#fff' : '#111827';
  const track = isDark ? '#2e2e33' : '#eef0f3';

  return (
    <div
      data-testid="plan-limits-panel"
      style={{ padding: '8px 14px 10px', borderBottom: `1px solid ${isDark ? '#2e2e33' : '#f3f4f6'}` }}
    >
      <LocalCapsSection rows={localRows} isDark={isDark} onClose={onClose} />

      {hasCloud && <div style={{ fontSize: 11, fontWeight: 600, color: strong }}>{t('planLimits.title')}</div>}

      {lastRefusal && (
        <div
          role="status"
          data-testid="plan-limit-refusal"
          style={{
            marginTop: 6,
            padding: '6px 8px',
            borderRadius: 6,
            fontSize: 10,
            lineHeight: 1.45,
            background: isDark ? 'rgba(245,158,11,0.12)' : 'rgba(245,158,11,0.1)',
            color: isDark ? '#fbbf24' : '#b45309',
          }}
        >
          {t(`planLimits.refused.${lastRefusal.code}`)}
          <button
            type="button"
            onClick={dismiss}
            style={{ marginLeft: 6, border: 'none', background: 'transparent', color: 'inherit', cursor: 'pointer', fontSize: 10, textDecoration: 'underline', padding: 0 }}
          >
            {t('planLimits.dismiss')}
          </button>
        </div>
      )}

      {deviceNotice && (
        <div
          role="status"
          data-testid="plan-limit-device-notice"
          style={{
            marginTop: 6,
            padding: '6px 8px',
            borderRadius: 6,
            fontSize: 10,
            lineHeight: 1.45,
            background: isDark ? 'rgba(99,102,241,0.14)' : 'rgba(99,102,241,0.1)',
            color: isDark ? '#a5b4fc' : '#4338ca',
          }}
        >
          {t(`planLimits.onThisDevice.${deviceNotice.kind}`)}
          <button
            type="button"
            onClick={dismissDeviceNotice}
            style={{ marginLeft: 6, border: 'none', background: 'transparent', color: 'inherit', cursor: 'pointer', fontSize: 10, textDecoration: 'underline', padding: 0 }}
          >
            {t('planLimits.dismiss')}
          </button>
        </div>
      )}

      {rows.map((row) => (
        <div key={row.key} data-testid={`plan-limit-${row.key}`} style={{ marginTop: 8 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 10, color: muted }}>
            <span>{t(`planLimits.rows.${row.key}`)}</span>
            <span style={{ color: row.state === 'ok' ? muted : BAR_COLOR[row.state], fontVariantNumeric: 'tabular-nums' }}>
              {t('planLimits.usedOf', { used: row.usedLabel, limit: row.limitLabel })}
            </span>
          </div>
          <div
            role="progressbar"
            aria-label={t(`planLimits.rows.${row.key}`)}
            aria-valuemin={0}
            aria-valuemax={100}
            aria-valuenow={Math.round(row.share * 100)}
            style={{ marginTop: 3, height: 5, borderRadius: 9999, background: track, overflow: 'hidden' }}
          >
            <div style={{ width: `${Math.max(row.share * 100, row.used > 0 ? 3 : 0)}%`, height: '100%', background: BAR_COLOR[row.state] }} />
          </div>
        </div>
      ))}

      {hasCloud && <div style={{ marginTop: 8, fontSize: 10, color: muted, lineHeight: 1.45 }}>{t('planLimits.hint')}</div>}
      <button
        type="button"
        onClick={() => {
          setUpgradeReason(t('planLimits.upgradeReason'));
          setUpgradeModalOpen(true);
          onClose?.();
        }}
        style={{ marginTop: 8, fontSize: 10, fontWeight: 600, padding: '3px 10px', borderRadius: 6, border: 'none', cursor: 'pointer', background: isDark ? '#4f46e5' : '#6366f1', color: '#fff' }}
      >
        {t('planLimits.upgrade')}
      </button>
    </div>
  );
}
